import { noop } from 'lodash';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ReducerState } from '../stateUtils/reducerTypes';
import {
  getIsDragglableUtil,
  getIsDropValid,
  getRenderPlaceholderPositionUtil,
} from './utils';
import {
  DndContextState,
  SetDraggedRowOptions,
  SetOverRuleOptions,
} from './types';

const defaultDndState: DndContextState = {
  draggedRowId: null,
  draggedRowType: null,
  overRuleId: null,
  position: null,
  setDraggedRow: noop,
  setDragOverRule: noop,
  getRenderPlaceholderPosition: () => null,
  getIsDragglable: () => false,
  resetState: noop,
};

const DndContext = createContext<DndContextState>({
  ...defaultDndState,
});

export const DndContextProvider = ({
  children,
  queryBuilderState,
  onDrop,
}: {
  children: ReactNode;
  queryBuilderState: ReducerState;
  onDrop: (dndContext: DndContextState) => void;
}) => {
  // remove duplications of setState, compact it to single useState with object
  const [draggedRowId, setDraggedRowId] = useState<string | null>(null);
  const [draggedRowType, setDraggedRowType] = useState<'rule' | 'query' | null>(
    null
  );
  const [overRuleId, setDragOverRuleId] = useState<string | null>(null);
  const [position, setPosition] = useState<'above' | 'below' | null>(null);
  const setDraggedRow = useCallback((props: SetDraggedRowOptions) => {
    setDraggedRowId(props.draggedRowId);
    setDraggedRowType(props.draggedRowType);
  }, []);
  const resetState = useCallback(() => {
    setDraggedRowId(null);
    setDraggedRowType(null);
    setDragOverRuleId(null);
    setPosition(null);
  }, []);
  const setDragOverRule = useCallback(
    (options: SetOverRuleOptions) => {
      if (draggedRowId) {
        setDragOverRuleId(options.overRuleId);
        setPosition(options.position);
      }
    },
    [draggedRowId]
  );
  const getRenderPlaceholderPosition = useCallback(
    (rowId: string) =>
      getRenderPlaceholderPositionUtil(
        rowId,
        {
          draggedRowId,
          draggedRowType,
          overRuleId,
          position,
        },
        queryBuilderState
      ),
    [queryBuilderState, overRuleId, position, draggedRowId, draggedRowType]
  );

  const getIsDragglable = useCallback(
    (rowId: string) => getIsDragglableUtil(rowId, queryBuilderState),
    [queryBuilderState]
  );

  const contextProps: DndContextState = {
    draggedRowId,
    draggedRowType,
    overRuleId,
    position,
    setDragOverRule,
    setDraggedRow,
    resetState,
    getRenderPlaceholderPosition,
    getIsDragglable,
  };

  useEffect(() => {
    const handleDragEnd = () => {
      if (getIsDropValid(contextProps, queryBuilderState)) {
        onDrop(contextProps);
      }
      resetState();
    };
    document.addEventListener('dragend', handleDragEnd, true);
    // Disable native drop animation
    const preventDragOverEvent = (e: DragEvent) => e.preventDefault();
    document.addEventListener('dragover', preventDragOverEvent);

    return () => {
      document.removeEventListener('dragover', preventDragOverEvent);
      document.removeEventListener('dragend', handleDragEnd, true);
    };
  });
  return (
    <DndContext.Provider value={contextProps}>{children}</DndContext.Provider>
  );
};

export const useDndContext = () => useContext(DndContext);
